import { Button, Checkbox, message, Divider, Badge, Tooltip, AutoComplete, Input } from 'antd';
import { WindowsFilled, AndroidFilled, AppleFilled, QqCircleFilled, SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';
import React, { useState, useRef, Fragment, useEffect } from 'react';
import { FooterToolbar } from '@ant-design/pro-layout';
import type { ProColumns, ActionType } from '@ant-design/pro-table';
import ProTable from '@ant-design/pro-table';
import { ModalForm, ProFormTextArea, ProFormText, ProForm } from '@ant-design/pro-form';
import { devices, updateDevice, enableDevices, delPeer, names as fetchNames, getUsedCount, delPeers, setDevicesNote } from '@/services/api';
import { API } from '@/services/typings';
import { useModel } from '@umijs/max';
import { t, renderDetails, getTablePaginationConfig, GroupTypeDevice, NamesPurpose_AssignDeviceToUser, NamesPurpose_ChangeDeviceGroup, NamesPurpose_UpdateDevice } from '../../global';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { DelConfirmForm } from '../UserList/index';
import { getLocalSortState, handleSortStateChange, TableSorterTooltip } from '@/components/Others/table_sort';
import { getUsedDevices, getUsedDevicesColor } from '../Welcome';
import SetNoteModal from '../AddressBook/DetailComponents/SetNoteModal';
import {
  canEditDeviceInfo,
  canEnableDisableDevice,
  canDeleteDevice,
  hasGlobalPermAssignDeviceToUser,
  hasGlobalPermChangeDeviceGroup,
  shouldDeviceListShowMultiSelect,
  canAssignStrategyToDevice,
} from '../RoleList/permissionUtils';

const hideUsenameKey = "hideUsernameOnDeviceColumn";
export const DEVICE_LIST_SORT_STATE_KEY = 'device_list_sort_state';
const ID_SORT_KEY = 'device_list_id_sort_key';

const DeviceTip: React.FC<{ actionRef: any }> = (props) => {
  const [hideUsername, setHideUsername] = useState(false);

  useEffect(() => {
    const storedHideUsername = localStorage.getItem(hideUsenameKey) === 'true';
    setHideUsername(storedHideUsername);
  }, []);

  const handleCheckboxChange = (e: any) => {
    const checked = e.target.checked;
    setHideUsername(checked);
    localStorage.setItem(hideUsenameKey, checked.toString());
    props.actionRef?.current?.reload();
  };

  return <div onClick={(e) => e.stopPropagation()}>
    <div style={{ margin: 8 }}>
      <Checkbox
        checked={hideUsername}
        onChange={handleCheckboxChange}
        style={{ color: 'white' }}
      > {t('Hide username')}</Checkbox>
    </div>
    {!hideUsername && <span>{t('username@device_name')}</span>}
  </div>
};

const handleUpdate = async (fields: API.DeviceListItem, old: API.DeviceListItem) => {
  let data = { guid: old.guid } as any;
  data['id'] = old.id;
  if ((fields.note || '') != (old.note || '')) {
    data['note'] = fields.note;
  }
  if ((fields.info?.device_name || '') != (old.info?.device_name || '')) {
    data['device_name'] = fields.info?.device_name;
  }
  if ((fields.user_name || '') != (old.user_name || '')) {
    data['user_name'] = fields.user_name;
  }
  if ((fields.device_group_name || '') != (old.device_group_name || '')) {
    data['device_group_name'] = fields.device_group_name;
  }
  if ((fields.strategy_name || '') != (old.strategy_name || '')) {
    data['strategy_name'] = fields.strategy_name || '';
  }
  if (Object.keys(data).length == 2) return;
  const hide = message.loading(t('Updating'));
  try {
    await updateDevice({ data });
    hide();

    message.success(t('Update is successful'));
    return true;
  } catch (error) {
    hide();
    message.error(
      typeof error == 'string' ? (error as string) : t('Update failed, please try again!'),
    );
    return false;
  }
};

export const handleEnable = async (rows: API.DeviceListItem[], disable: boolean) => {
  const hide = message.loading(t('Updating'));
  try {
    await enableDevices({
      data: {
        rows: rows.map((x) => x.guid),
        disable,
      },
    });
    hide();

    message.success(t('Update is successful'));
    return true;
  } catch (error) {
    hide();
    message.error(
      typeof error == 'string' ? (error as string) : t('Enable/disable failed, please try again!'),
    );
    return false;
  }
};

const DevicesTable: React.FC = () => {

  const [updateModalVisible, handleUpdateModalVisible] = useState<boolean>(false);
  const [delConfirmModalVisible, handleDelConfirmModalVisible] = useState<boolean>(false);
  const [batchDeleteModalVisible, handleBatchDeleteModalVisible] = useState<boolean>(false);

  const [showDetail, setShowDetail] = useState<boolean>(false);

  const actionRef = useRef<ActionType>();
  const [currentRow, setCurrentRow] = useState<API.DeviceListItem>();
  const [selectedRowsState, setSelectedRows] = useState<API.DeviceListItem[]>([]);
  const { initialState } = useModel('@@initialState');
  const is_admin = initialState?.user?.is_admin;
  const deploymentRequiredValue = initialState?.settings?.find((entry) => entry.key === 'DEPLOYMENT_REQUIRED')?.value?.toUpperCase();
  const deploymentRequired = deploymentRequiredValue === 'TRUE' || deploymentRequiredValue === 'Y';
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [sortState, setSortState] = useState<API.SortState>(() => getLocalSortState(DEVICE_LIST_SORT_STATE_KEY));
  const [usedCount, setUsedCount] = useState<API.UsedCount | undefined>();
  const [setNoteModalVisible, setSetNoteModalVisible] = useState<boolean>(false);
  const canAssignUser = hasGlobalPermAssignDeviceToUser(initialState?.user);
  const canChangeGroupOfDevice = hasGlobalPermChangeDeviceGroup(initialState?.user);
  const showMultiSelect = shouldDeviceListShowMultiSelect(initialState?.user);

  useEffect(() => {
    actionRef.current?.reload();
  }, []);

  useEffect(() => {
    refreshUsedCount();
  }, [is_admin]);

  const refreshUsedCount = () => {
    if (is_admin) {
      getUsedCount().then(data => {
        setUsedCount(data);
      });
    } else {
      setUsedCount(undefined);
    }
  }

  const columns = deviceColumns({
    actionRef,
    user: initialState?.user,
    onIdClick: (entity: API.DeviceListItem) => {
      setCurrentRow(entity);
      setShowDetail(true);
    },
    totalRecords,
    extraColumns: [
      {
        title: t('Action', true),
        search: false,
        hideInDescriptions: true,
        width: '15%',
        render: (text, record) => {
          // Check permissions for each operation

          const canEditInfo = canEditDeviceInfo(initialState?.user, record);
          const canEdit = canEditInfo || canAssignUser || canChangeGroupOfDevice || canAssignStrategyToDevice(initialState?.user, record);
          const canEnableDisable = canEnableDisableDevice(initialState?.user, record);
          const canDelete = canDeleteDevice(initialState?.user, record) && !isNormal(record) && (!record.is_online || deploymentRequired);

          // Build actions array with dividers
          const actions: React.ReactNode[] = [];

          if (canEdit) {
            actions.push(
              <a
                key="edit"
                onClick={() => {
                  setCurrentRow(record);
                  handleUpdateModalVisible(true);
                }}
              >
                {t('Edit')}
              </a>
            );
          }

          if (canEnableDisable) {
            if (actions.length > 0) actions.push(<Divider key="div1" type="vertical" />);
            actions.push(
              <a
                key="enable"
                style={isNormal(record) ? { color: "red" } : {}}
                onClick={async () => {
                  setCurrentRow(record);
                  await handleEnable([record], isNormal(record));
                  actionRef.current?.reloadAndRest?.();
                  refreshUsedCount();
                }}
              >
                {t(isNormal(record) ? 'Disable' : 'Enable')}
              </a>
            );
          }

          if (canDelete) {
            if (actions.length > 0) actions.push(<Divider key="div2" type="vertical" />);
            actions.push(
              <a
                key="delete"
                style={{ color: "red" }}
                onClick={async () => {
                  setCurrentRow(record);
                  handleDelConfirmModalVisible(true);
                }}
              >
                {t('Delete')}
              </a>
            );
          }

          return (
            <Fragment>
              {actions.length > 0 ? actions : <span style={{ color: '#999' }}>-</span>}
            </Fragment>
          );
        }
      },
    ],
    sortState,
    setSortState,
  })

  const updateForm = (
    visible: boolean,
    handleModalVisible: React.Dispatch<React.SetStateAction<boolean>>,
    actionRef: React.MutableRefObject<ActionType | undefined>,
    initialValues: API.DeviceListItem | {},
  ) => {
    if (!visible) return null;

    const canEditInfo = canEditDeviceInfo(initialState?.user, currentRow);
    const canAssignStrategy = canAssignStrategyToDevice(initialState?.user, currentRow);


    // Process strategy_name: extract only device strategy (first part before ' / ')
    const processedInitialValues = { ...initialValues };
    if ('strategy_name' in processedInitialValues) {
      const strategyName = processedInitialValues.strategy_name;
      if (strategyName === '-') {
        // If it's '-', set to null/empty
        processedInitialValues.strategy_name = undefined;
      } else if (strategyName && typeof strategyName === 'string' && strategyName.includes(' / ')) {
        // If it's 'a / b / c' format, extract only the first part (device strategy)
        processedInitialValues.strategy_name = strategyName.split(' / ')[0];
        // If the first part is '-', set to null/empty
        if (processedInitialValues.strategy_name === '-') {
          processedInitialValues.strategy_name = undefined;
        }
      }
    }

    return (
      <ModalForm
        title={t('Edit')}
        visible={visible}
        width="400px"
        initialValues={processedInitialValues}
        onVisibleChange={handleModalVisible}
        onFinish={async (value) => {
          const success = await handleUpdate(
            value as API.DeviceListItem,
            processedInitialValues as API.DeviceListItem,
          );
          if (success) {
            handleModalVisible(false);
            if (actionRef.current) {
              actionRef.current.reload();
            }
            if (initialState?.license?.max_conns) {
              refreshUsedCount();
            }
          }
        }}
      >
        <NameEditor
          table="user"
          field="user_name"
          label="User"
          purpose={NamesPurpose_AssignDeviceToUser}
          disable={!canAssignUser}
        />
        <ProFormText
          fieldProps={{ autoComplete: 'off' }}
          rules={[
            {
              whitespace: true,
              required: true,
            },
            {
              max: 300,
            },
          ]}
          width="md"
          name={["info", "device_name"]}
          label={t('Device Name', true)}
          disabled={!canEditInfo}
        />
        <NameEditor
          table="grp"
          field="device_group_name"
          label="Group"
          required={false}
          type={GroupTypeDevice}
          disable={!canChangeGroupOfDevice}
          purpose={NamesPurpose_ChangeDeviceGroup}
        />
        <NameEditor
          table="strategy"
          field="strategy_name"
          label="Strategy"
          disable={!canAssignStrategy}
          purpose={NamesPurpose_UpdateDevice}
        />
        <ProFormTextArea
          fieldProps={{ autoComplete: 'off' }}
          width="md"
          name="note"
          rules={[
            {
              max: 300,
            },
          ]}
          label={t('Note', true)}
          disabled={!canEditInfo}
        />
      </ModalForm>
    );
  };

  const getHeaderTitle = () => {
    if (is_admin) {
      return <Tooltip title={t('used_device')}>
        <span>
          {t('Device List', true)} (
          <span style={{ backgroundColor: getUsedDevicesColor(usedCount, initialState?.license) }}>
            {getUsedDevices(usedCount, initialState?.license)}
          </span>
          )
        </span>
      </Tooltip>;
    } else {
      return t('Device List', true);
    }
  }

  const handleDelete = async (guid: string) => {
    await delPeer(guid);
    if (initialState?.license?.max_conns) {
      refreshUsedCount();
    }
  }

  const getBatchDeleteButton = () => {
    return (
      selectedRowsState.every((x) => (!x.is_online || deploymentRequired) && !isNormal(x)) ? (
        <Tooltip title={deploymentRequired ? t('batch_delete_device_tooltip_deployment') : t('batch_delete_device_tooltip')}>
          <Button type="primary" onClick={() => {
            handleBatchDeleteModalVisible(true);
          }}>
            {t('Delete')}
          </Button>
        </Tooltip>
      ) : undefined
    )
  }

  const getBatchDeleteInitialValues = () => {
    return selectedRowsState.length > 0 ? {
      id: selectedRowsState[selectedRowsState.length - 1]?.id,
      guid: selectedRowsState.map((x) => x.guid),
    } : undefined
  }

  const handleBatchDelete = async (guids: string[]) => {
    let error = undefined;
    try {
      await delPeers({
        data: {
          guids,
        },
      });
    } catch (e) {
      error = e;
    } finally {
      setSelectedRows([]);
      actionRef.current?.reloadAndRest?.();
      if (initialState?.license?.max_conns) {
        refreshUsedCount();
      }
      // catch error to throw it to the confirm modal
      if (error) {
        throw error;
      }
    }
  }

  const setNoteModal = () => {
    return <SetNoteModal
      visible={setNoteModalVisible}
      setModalVisible={setSetNoteModalVisible}
      actionRef={actionRef}
      guids={selectedRowsState.map((x) => x.guid)}
      setFunction={async (guids, note) => {
        await setDevicesNote({ data: { guids, note } });
        setSelectedRows([]);
      }}
    ></SetNoteModal >;
  }

  return (
    <div>
      <ProTable<API.DeviceListItem, API.PageParams>
        headerTitle={getHeaderTitle()}
        actionRef={actionRef}
        columnsState={{ persistenceType: 'localStorage', persistenceKey: 'devicelist_columns_state' }}
        rowKey="id"
        search={{
          labelWidth: 120,
        }}
        request={async (params, sort, _filter) => {
          const result = await devices(params, sort);
          setTotalRecords(result.total || 0);
          return result;
        }}
        columns={columns}
        rowSelection={
          showMultiSelect ? {
            onChange: (_, selectedRows) => {
              setSelectedRows(selectedRows);
            },
          } : undefined
        }
        pagination={getTablePaginationConfig("device_list")}
        onChange={(_, __, sorter) => {
          handleSortStateChange(sorter, actionRef, DEVICE_LIST_SORT_STATE_KEY, setSortState);
        }}
      />
      {selectedRowsState?.length > 0 && (() => {
        // Check if all selected devices have the required permissions
        const allCanEnableDisable = selectedRowsState.every(record =>
          canEnableDisableDevice(initialState?.user, record)
        );
        const allCanEditNote = selectedRowsState.every(record =>
          canEditDeviceInfo(initialState?.user, record)
        );
        const allCanDelete = selectedRowsState.every(record =>
          canDeleteDevice(initialState?.user, record) && !isNormal(record) && (!record.is_online || deploymentRequired)
        );

        // Show toolbar if user has any permission on selected items
        const hasAnyPermission = allCanEnableDisable || allCanEditNote || allCanDelete;

        if (!hasAnyPermission) return null;

        return (
          <FooterToolbar
            extra={
              <div>
                {t('Chosen')} <a style={{ fontWeight: 600 }}>{selectedRowsState.length}</a>{' '}
                {t('Items')}
              </div>
            }
          >
            {allCanEnableDisable && [true, false].map((v) => (
              <Button
                key={v ? 'enable' : 'disable'}
                onClick={async () => {
                  await handleEnable(selectedRowsState, !v);
                  setSelectedRows([]);
                  actionRef.current?.reloadAndRest?.();
                  refreshUsedCount();
                }}
              >
                {v ? t('Enable') : t('Disable')}
              </Button>
            ))}
            {allCanEditNote && (
              <Button onClick={() => setSetNoteModalVisible(true)}>
                {t('set_note', true)}
              </Button>
            )}
            {allCanDelete && getBatchDeleteButton()}
          </FooterToolbar>
        );
      })()}
      {updateForm(updateModalVisible, handleUpdateModalVisible, actionRef, currentRow || {})}
      {renderDetails(
        showDetail,
        currentRow?.guid,
        currentRow,
        columns.slice(0, columns.length - 1).filter((x) => x.title),
        setCurrentRow,
        setShowDetail,
      )}
      {delConfirmModalVisible && <DelConfirmForm visible={delConfirmModalVisible} handleModalVisible={handleDelConfirmModalVisible} actionRef={actionRef} initialValues={currentRow || {}} del={handleDelete} />}
      {batchDeleteModalVisible && <DelConfirmForm visible={batchDeleteModalVisible} handleModalVisible={handleBatchDeleteModalVisible} actionRef={actionRef} initialValues={getBatchDeleteInitialValues() || {}} del={handleBatchDelete} />}
      {setNoteModal()}
    </div>
  );
};

function isNormal(r: API.DeviceListItem) {
  return r.status == 1;
}

export const NameEditor = (props: { table: string, field: string, label: string, required?: boolean, type?: number, disable?: boolean, purpose: string }) => {
  const [names, setNames] = useState<{ value: string }[] | undefined>(undefined);
  const fetch = async (pattern: string) => {
    const g = await fetchNames({ table: props.table, limit: 30, pattern, type: props.type, purpose: props.purpose });
    const namesValue = (g || []).map((x) => ({ value: x }));
    let sortedNames = namesValue;
    try {
      const locale = typeof navigator !== 'undefined'
        ? (navigator.languages?.[0] || navigator.language)
        : undefined;
      const collator = new Intl.Collator(locale, { numeric: true, sensitivity: 'base', ignorePunctuation: true });
      sortedNames = [...namesValue].sort((a, b) => collator.compare(a.value, b.value));
    } catch (err) {
      console.warn('Failed to sort names:', err);
    }
    setNames(sortedNames);
  };
  const handleSearch = (value: string) => {
    fetch(value + '%');
  };
  if (names == undefined) fetch('%');

  return (
    <ProForm.Item
      rules={[
        {
          whitespace: true,
          required: props.required,
        },
      ]}
      name={props.field}
      label={t(props.label, true)}
    >
      <AutoComplete allowClear={true} options={names} style={{ width: 328 }} onSearch={handleSearch} disabled={props.disable}>
        <Input suffix={<SearchOutlined />} placeholder={t("Search", true) as string} />
      </AutoComplete>
    </ProForm.Item>
  );
};

export const deviceColumns = (props: {
  actionRef: any,
  is_select?: boolean,
  user?: API.User,
  onIdClick?: ((entity: API.DeviceListItem) => void),
  extraColumns?: ProColumns<API.DeviceListItem>[],
  totalRecords: number,
  sortState: API.SortState,
  setSortState: React.Dispatch<React.SetStateAction<API.SortState>>
}) => {
  const idSortKey = localStorage.getItem(ID_SORT_KEY) || 'a_id';
  const onlineSortLimit = 2000;
  const sortOptions = [
    { value: "a_id", label: "ID" },
    { value: "a_info_os", label: t("OS", true) },
    {
      value: "a_last_online",
      label: <span>
        {t('last_online', true)} <Tooltip title={t("last_online_sort_tip", true)}>
          <InfoCircleOutlined style={{ color: '#1890ff', marginLeft: 4 }} />
        </Tooltip>
      </span>,
      disabled: !props.totalRecords || props.totalRecords > onlineSortLimit
    },
  ];

  const getOfflineDuration = (lastOnlineTime: string): string => {
    try {
      const lastOnline = new Date(lastOnlineTime + (lastOnlineTime.endsWith('Z') ? '' : 'Z'));
      const now = new Date();
      const diffMs = now.getTime() - lastOnline.getTime();

      if (diffMs > 0) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays > 0) {
          return ` (${diffDays} ${t('days', true)})`;
        } else if (diffHours > 0) {
          return ` (${diffHours} ${t('hours', true)})`;
        } else if (diffMinutes > 0) {
          return ` (${diffMinutes} ${t('minutes', true)})`;
        } else {
          return ` (${t('just now', true)})`;
        }
      } else {
        return '';
      }
    } catch (error) {
      return '';
    }
  };

  const onlineTooltip = (entity: API.DeviceListItem) => {
    if (entity.is_online) {
      return t('Online');
    } else {
      const offlineDuration = entity.last_online ? getOfflineDuration(entity.last_online) : '';
      return `${t('Offline', true)}${offlineDuration}`;
    }
  }

  const columns: ProColumns<API.DeviceListItem>[] = [
    {
      title: t('ID', true), // plain=true is necessary, otherwise will crash
      dataIndex: 'id',
      width: '15%',
      render: (dom, entity) => {
        const os = (entity.info?.os || '').split(' / ');
        return (<>
          <Tooltip title={onlineTooltip(entity)}>
            <Badge status={entity.is_online ? 'success' : 'error'} />&nbsp;&nbsp;
          </Tooltip>
          {os[0] && <Tooltip title={os[1]}>
            {os[0] == 'windows' && <WindowsFilled />}
            {(entity.info?.os || '').toLowerCase().indexOf('linux') >= 0 && <QqCircleFilled />}
            {os[0] == 'android' && <AndroidFilled />}
            {os[0] == 'macos' && <AppleFilled />}
            {os[0] == 'ios' && <AppleFilled />}
            &nbsp;&nbsp;
          </Tooltip>}
          {props.onIdClick ? <a
            onClick={() => props.onIdClick?.(entity)}
          >
            {dom}
          </a> : <span>{dom}</span>}</>

        );
      },
      key: idSortKey,
      sorter: true,
      sortOrder: props.sortState.columnKey === idSortKey ? props.sortState.order : undefined,
      showSorterTooltip: {
        title: <TableSorterTooltip actionRef={props.actionRef} idSortKey={ID_SORT_KEY} sortStateKey={DEVICE_LIST_SORT_STATE_KEY} options={sortOptions as any} setSortState={props.setSortState} />,
      },
      hideInSearch: true,
    },
    {
      title: t('ID', true),
      dataIndex: 'id',
      hideInTable: true,
      hideInDescriptions: true,
    },
    {
      title: t('Device Name', true),
      dataIndex: 'device_name',
      hideInTable: true,
      hideInDescriptions: true,
    },
    {
      title: t('Device Username', true),
      dataIndex: 'device_username',
      hideInTable: true,
      hideInDescriptions: true,
    },
    {
      title: t('Device', true),
      tooltip: <DeviceTip actionRef={props.actionRef} />,
      hideInSearch: true,
      render: (dom, entity) => {
        const hideUsername = localStorage.getItem(hideUsenameKey) === 'true';
        if (entity.info?.username && entity.info?.device_name && !hideUsername) {
          return entity.info?.username + '@' + entity.info?.device_name;
        } else {
          return entity.info?.device_name;
        }
      },
      key: 'a_info_username',
      sorter: true,
      sortOrder: props.sortState.columnKey === 'a_info_username' ? props.sortState.order : undefined,
      showSorterTooltip: false,
    },
    {
      title: t('Group', true),
      dataIndex: 'device_group_name',
      hideInSearch: true,
      key: 'g_name',
      sorter: true,
      sortOrder: props.sortState.columnKey === 'g_name' ? props.sortState.order : undefined,
      showSorterTooltip: false,
    },
    {
      title: t('Group', true),
      dataIndex: 'device_group_name',
      hideInTable: true,
      hideInSetting: true,
      hideInDescriptions: true,
    },
    {
      title: t('User', true),
      dataIndex: 'user_name',
      hideInSearch: true,
      key: 'b_name',
      sorter: true,
      sortOrder: props.sortState.columnKey === 'b_name' ? props.sortState.order : undefined,
      showSorterTooltip: false,
    },
    {
      title: t('User', true),
      dataIndex: 'user_name',
      hideInTable: true,
      hideInDescriptions: true,
    },
    {
      title: t('Status', true),
      dataIndex: 'status',
      hideInSearch: true,
      render: (text, record) => {
        if (!isNormal(record)) {
          return (
            <Tooltip title={t('Disabled')}>
              <CloseCircleOutlined style={{ color: '#f5222d' }} />
            </Tooltip>
          );
          return <Badge status="default" text={t('Normal')} />;
        } else {
          return (
            <Tooltip title={t('Normal')}>
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            </Tooltip>
          );
        }
      },
      key: 'a_status',
      sorter: true,
      sortOrder: props.sortState.columnKey === 'a_status' ? props.sortState.order : undefined,
      showSorterTooltip: false,
    },
    {
      title: t('Status', true),
      dataIndex: 'status',
      valueEnum: {
        1: {
          text: t('Normal'),
          status: 'Default',
        },
        0: {
          text: t('Disabled'),
          status: 'Disabled',
        },
      },
      hideInSearch: props.is_select,
      hideInTable: true,
      hideInDescriptions: true,
    },
    {
      title: t('Strategy', true),
      dataIndex: 'strategy_name',
      hideInSearch: true,
      tooltip: `${t('strategy_tooltip', true, 'Format: "Device Strategy / User Strategy / Device Group Strategy". A single \'-\' means default strategy, while \'-\' in a position means no strategy is set for that level.')}`,
      key: 'd_name',
      sorter: false,
      sortOrder: props.sortState.columnKey === 'd_name' ? props.sortState.order : undefined,
      showSorterTooltip: false,
    },
    {
      title: t('Device Strategy', true),
      dataIndex: 'strategy_name',
      hideInDescriptions: true,
      hideInTable: true,
    },
    {
      title: t('User Strategy', true),
      dataIndex: 'user_strategy_name',
      hideInDescriptions: true,
      hideInTable: true,
    },
    {
      title: t('Group Strategy', true),
      dataIndex: 'device_group_strategy_name',
      hideInDescriptions: true,
      hideInTable: true,
    },
    {
      title: t('IP', true),
      dataIndex: 'ip',
      hideInTable: true,
      hideInDescriptions: true,
    },
    {
      title: t('Version', true),
      dataIndex: 'version',
      hideInTable: true,
      hideInDescriptions: true,
    },
    {
      title: t('Info', true),
      dataIndex: 'info',
      valueType: 'textarea',
      search: false,
      ellipsis: true,
      render: (text, record) => {
        let txt = '';
        try {
          let info = record.info;
          if (!info) return '';
          if (info.ip) {
            txt += 'ip: ' + info.ip.replace('::ffff:', '') + '; ';
          }
          if (info.version) {
            txt += 'version: ' + info.version + '; ';
          }
          if (info.version) {
            txt += 'cpu: ' + info.cpu + '; ';
          }
          if (info.os) {
            txt += 'mem: ' + info.memory + '; ';
          }
        } catch (e) { }
        return txt;
      },
    },
    {
      title: t('Note', true),
      dataIndex: 'note',
      valueType: 'textarea',
      search: false,
      hideInSearch: true,
      ellipsis: true,
      key: 'a_note',
      sorter: true,
      sortOrder: props.sortState.columnKey === 'a_note' ? props.sortState.order : undefined,
      showSorterTooltip: false,
    },
    ...props.extraColumns || [],
  ];

  return columns;
}
export default DevicesTable;
